/**
 * The live scanner: the camera stays open, and a card that is framed and still is
 * taken without a press.
 *
 * This is the ManaBox-shaped surface the plan set out to build. Three things make it
 * behave differently from the batch camera it replaces:
 *
 *  - **the viewfinder never closes.** Opening the camera is one act at the start of a
 *    session; every card after that is a look at the same stream.
 *  - **nothing waits for a press.** `LiveSession` fires once a card has been framed
 *    and still for a few looks, then waits for it to leave before firing again.
 *  - **the guide shows the surface, not just the card.** The card outline is inset
 *    from the viewfinder on purpose: the detector estimates the surface per row from
 *    the frame's own edges (ADR 0008), so a card that fills the frame edge-to-edge
 *    cannot be found. The margin is the detector's line of sight.
 *
 * The sheet is the same component on the web and in the shell; only the camera source
 * differs. On the shell the sheet is transparent over the native preview, which is
 * why the page's own background is removed while it is open (`native-camera-live`).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CameraSource } from './camera-source.js';
import type { CaptureRead } from './capture.js';
import { LiveSession, LIVE_HINTS, type LiveDecision } from './live.js';

/** What the batch did with the capture the scanner took. */
export interface LiveCaptureOutcome {
  status: 'accepted' | 'refused';
  /** The one line the sheet shows: the printing, or why there is not one. */
  label: string;
}

export interface LiveScannerProps {
  /**
   * Builds the camera for this device. The sheet hands it the viewfinder element,
   * which exists by the time this is called: the source is created in an effect,
   * after the sheet has rendered.
   */
  source: (video: HTMLVideoElement | null) => CameraSource | null;
  /** Hands the read to the batch. Resolves with what to show under the guide. */
  onCapture: (read: CaptureRead, index: number) => Promise<LiveCaptureOutcome> | LiveCaptureOutcome;
  /** First capture number, so labels continue across sittings. */
  startAt?: number;
  /** How often the frame is looked at, in milliseconds. Lowered in tests. */
  intervalMs?: number;
}

type Status = 'starting' | 'live' | 'unavailable';

/** A short buzz per accepted card: ManaBox's feedback, without shipping audio. */
const vibrate = (): void => {
  try {
    navigator.vibrate?.(12);
  } catch {
    // A device without a vibration motor is not a failed scan.
  }
};

const readDebug = (): boolean => {
  try {
    return window.localStorage.getItem('unsorted.scannerDebug') === '1';
  } catch {
    return false;
  }
};

export function LiveScanner({ source, onCapture, startAt = 1, intervalMs = 120 }: LiveScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const sourceRef = useRef<CameraSource | null>(null);
  const sessionRef = useRef<LiveSession>(new LiveSession());
  const firingRef = useRef(false);
  const counterRef = useRef(startAt);
  const tapRef = useRef<{ at: number; x: number; y: number } | null>(null);
  const tapTimer = useRef<number | null>(null);
  const fireRef = useRef<() => Promise<void>>(async () => undefined);

  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>('starting');
  const [sourceKind, setSourceKind] = useState<'browser' | 'native' | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [decision, setDecision] = useState<LiveDecision>({
    capture: false,
    state: 'empty',
    hint: LIVE_HINTS.empty,
    progress: 0,
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [taken, setTaken] = useState(0);
  const [busy, setBusy] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [debug] = useState(readDebug);

  const close = useCallback((): void => {
    if (tapTimer.current !== null) {
      window.clearTimeout(tapTimer.current);
      tapTimer.current = null;
    }
    setOpen(false);
    setNotice(null);
    setTorchOn(false);
    setTorchAvailable(false);
    setDecision({ capture: false, state: 'empty', hint: LIVE_HINTS.empty, progress: 0 });
    // A sitting's counters belong to the sitting: reopening starts a fresh one.
    counterRef.current = startAt;
    setTaken(0);
  }, [startAt]);

  /** Open the camera once the sheet (and its viewfinder) is on screen. */
  useEffect(() => {
    if (!open) return;
    const created = source(videoRef.current);
    if (created === null) {
      setStatus('unavailable');
      setReason('this device has no camera the app can open');
      return;
    }
    sourceRef.current = created;
    setSourceKind(created.kind);
    setStatus('starting');
    sessionRef.current.reset();

    let cancelled = false;
    void (async () => {
      try {
        const { torch } = await created.start();
        if (cancelled) return;
        setTorchAvailable(torch);
        setStatus('live');
      } catch (failure) {
        if (cancelled) return;
        await created.stop().catch(() => undefined);
        sourceRef.current = null;
        setSourceKind(null);
        setStatus('unavailable');
        setReason(
          failure instanceof DOMException && failure.name === 'NotAllowedError'
            ? 'the camera permission was denied — allow the camera and try again, or use a single photo'
            : failure instanceof Error
              ? failure.message
              : 'the camera could not be opened',
        );
      }
    })();

    return () => {
      cancelled = true;
      if (sourceRef.current === created) sourceRef.current = null;
      void created.stop().catch(() => undefined);
      sessionRef.current.reset();
    };
  }, [open, source]);

  /** Take one frame now and hand it to the batch. */
  const fire = useCallback(async (): Promise<void> => {
    const current = sourceRef.current;
    if (current === null || firingRef.current) return;
    firingRef.current = true;
    setBusy(true);
    try {
      const read = await current.grab();
      if (read === null || read.artPhash === null) {
        // A framing failure, not a filed card: it does not join the batch, and the
        // session asks for the card to leave before trying again.
        sessionRef.current.refused(Date.now());
        setNotice('No card was found in that frame. Fill the guide, and keep the surface behind the card plain.');
        setDecision({ capture: false, state: 'waiting', hint: LIVE_HINTS.waiting, progress: 0 });
        return;
      }

      const outcome = await onCapture(read, counterRef.current);
      counterRef.current += 1;
      if (outcome.status === 'accepted') {
        sessionRef.current.accepted(Date.now());
        setTaken((count) => count + 1);
        vibrate();
      } else {
        sessionRef.current.refused(Date.now());
      }
      setNotice(outcome.label);
    } catch (failure) {
      sessionRef.current.refused(Date.now());
      setNotice(
        failure instanceof Error ? `That capture failed: ${failure.message}` : 'That capture failed. Try another look.',
      );
    } finally {
      firingRef.current = false;
      setBusy(false);
    }
  }, [onCapture]);

  useEffect(() => {
    fireRef.current = fire;
  }, [fire]);

  /** The live loop: look, decide, and fire when the policy says so. */
  useEffect(() => {
    if (!open || status !== 'live') return;
    let cancelled = false;
    let inFlight = false;
    const timer = window.setInterval(() => {
      const current = sourceRef.current;
      if (cancelled || inFlight || current === null || firingRef.current) return;
      inFlight = true;
      void (async () => {
        try {
          const observation = await current.read();
          if (cancelled) return;
          const next = sessionRef.current.observe(observation, Date.now());
          setDecision(next);
          if (next.capture) await fireRef.current();
        } catch {
          // A look that failed is not a scan: the next look is a few hundred
          // milliseconds away, and the sheet keeps showing the last thing it knew.
        } finally {
          inFlight = false;
        }
      })();
    }, intervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [open, status, intervalMs]);

  /**
   * While the sheet is open, the page behind it does not scroll.
   *
   * Not cosmetic: a card taken mid-batch grows the batch below, and a swipe meant for
   * the sheet's own controls would otherwise scroll the page under it - the frame
   * appears to jump after every capture.
   */
  useEffect(() => {
    if (!open) return;
    const body = document.body;
    const previous = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previous;
    };
  }, [open]);

  /** The page has to be transparent for the shell's preview to show through. */
  useEffect(() => {
    if (!open || sourceKind !== 'native') return;
    const root = document.documentElement;
    root.classList.add('native-camera-live');
    return () => root.classList.remove('native-camera-live');
  }, [open, sourceKind]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  useEffect(
    () => () => {
      if (tapTimer.current !== null) window.clearTimeout(tapTimer.current);
    },
    [],
  );

  const take = useCallback((): void => {
    if (!open || status !== 'live' || busy) return;
    const next = sessionRef.current.request(Date.now());
    if (!next.capture) return;
    setDecision(next);
    void fire();
  }, [open, status, busy, fire]);

  const toggleTorch = useCallback(async (): Promise<void> => {
    const current = sourceRef.current;
    if (current === null) return;
    const next = !torchOn;
    try {
      await current.setTorch(next);
      setTorchOn(next);
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : 'the light could not be switched');
    }
  }, [torchOn]);

  /**
   * The stage's tap gestures, as ManaBox has them: one tap takes the same card
   * again, two taps focus the lens where the finger landed.
   */
  const onStagePointerUp = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (status !== 'live') return;
    const bounds = event.currentTarget.getBoundingClientRect();
    // A pointer event with no coordinates is still a tap: some webviews deliver the
    // gesture without them, and a tap that lands in the middle is better than none.
    const pointX = Number.isFinite(event.clientX) ? event.clientX : bounds.left + bounds.width / 2;
    const pointY = Number.isFinite(event.clientY) ? event.clientY : bounds.top + bounds.height / 2;
    const x = bounds.width === 0 ? 0.5 : (pointX - bounds.left) / bounds.width;
    const y = bounds.height === 0 ? 0.5 : (pointY - bounds.top) / bounds.height;
    const now = Date.now();
    const last = tapRef.current;
    if (last !== null && now - last.at < 300 && Math.hypot(pointX - last.x, pointY - last.y) < 48) {
      tapRef.current = null;
      if (tapTimer.current !== null) {
        window.clearTimeout(tapTimer.current);
        tapTimer.current = null;
      }
      void sourceRef.current?.focusAt(x, y).catch(() => undefined);
      setNotice('Focused where you tapped.');
      return;
    }
    tapRef.current = { at: now, x: pointX, y: pointY };
    if (tapTimer.current !== null) window.clearTimeout(tapTimer.current);
    tapTimer.current = window.setTimeout(() => {
      tapTimer.current = null;
      take();
    }, 260);
  };

  if (!open) {
    return (
      <div className="row">
        <button type="button" className="primary" onClick={() => setOpen(true)}>
          Open the scanner
        </button>
        <span className="hint">
          Stays open: a card that fills the guide is taken by itself. Tap once to take the same card again, twice to
          focus.
        </span>
      </div>
    );
  }

  //
  // The sheet is rendered into `<body>` rather than where it sits in the tree, because
  // a dialog that covers the screen has to be the screen. As a child of the scan panel
  // it was sized by that panel: the panel animates its `transform` on mount, and a
  // transformed ancestor is the containing block for a `position: fixed` descendant, so
  // `inset: 0` meant "the panel" - which then grew taller with every card added to the
  // batch below it. The frame stretched and the bottom bar slid out of the viewport. A
  // portal has no such ancestor, and it stacks over the tab bar rather than inside the
  // stacking context the panel's animation created.
  return createPortal(
    <div className="scanner-sheet" role="dialog" aria-modal="true" aria-label="Live card scanner">
      <div
        className="scanner-stage"
        data-state={decision.state}
        onPointerUp={onStagePointerUp}
        aria-label="Scanner viewfinder"
        role="group"
      >
        <video
          ref={videoRef}
          className="scanner-video"
          playsInline
          muted
          autoPlay
          hidden={sourceKind !== 'browser'}
          aria-label="Camera viewfinder"
        />
        <div className="scanner-guide" aria-hidden="true">
          <span className="scanner-corner scanner-corner-tl" />
          <span className="scanner-corner scanner-corner-tr" />
          <span className="scanner-corner scanner-corner-bl" />
          <span className="scanner-corner scanner-corner-br" />
          <span className="scanner-progress" style={{ transform: `scaleX(${decision.progress})` }} />
        </div>

        <div className="scanner-readout">
          {status === 'starting' && (
            <p role="status" className="scanner-hint" aria-live="polite">
              Opening the camera…
            </p>
          )}
          {status === 'unavailable' && (
            <p role="status" className="scanner-hint">
              No camera here: {reason ?? 'the camera could not be opened'} — use a single photo instead.
            </p>
          )}
          {status === 'live' && (
            <>
              <p role="status" className="scanner-hint" aria-live="polite">
                {decision.hint}
              </p>
              {notice !== null && <p className="scanner-notice">{notice}</p>}
              {debug && decision.detail !== undefined && <p className="scanner-debug">{decision.detail}</p>}
            </>
          )}
        </div>
      </div>

      <div className="scanner-bar">
        <button type="button" className="primary" onClick={take} disabled={status !== 'live' || busy}>
          {busy ? 'Reading…' : 'Capture'}
        </button>
        <span className="hint tnum" role="status" aria-live="polite">
          {taken === 0 ? 'No cards yet' : `${taken} captured this sitting`}
        </span>
        {torchAvailable && (
          <button type="button" className="ghost" aria-pressed={torchOn} onClick={() => void toggleTorch()}>
            {torchOn ? 'Light off' : 'Light on'}
          </button>
        )}
        <button type="button" className="ghost" onClick={close}>
          Close
        </button>
      </div>
    </div>,
    document.body,
  );
}
